import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ListChecks } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useMe } from "@/hooks/queries/use-me";
import { TOOLBOX_PAGE_TITLE } from "@/lib/toolbox/type-scale";

interface RequireGroomingVisibleProps {
  children: ReactNode;
}

/**
 * Gate for every Grooming page (epics PI-SPEC-SW-E279, PI-SPEC-SW-E283).
 * Waits for GET /me, then shows "No Toolbox role yet" unless
 * `groomingVisible` is true: the person is a global admin or holds a Toolbox
 * role on at least one project. What each page then offers follows the
 * capabilities in GET /toolbox/access; the API keeps its own checks.
 */
export function RequireGroomingVisible({ children }: RequireGroomingVisibleProps) {
  const { data: meData, isLoading } = useMe();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" role="status" aria-live="polite">
        <div className="text-center space-y-3">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!meData?.groomingVisible) {
    return (
      <div className="mx-auto w-full max-w-[560px] px-4 py-16">
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
            <ListChecks className="h-8 w-8 text-muted-foreground" aria-hidden />
            <h1 className={TOOLBOX_PAGE_TITLE}>No Toolbox role yet</h1>
            <p className="text-sm text-muted-foreground">
              Grooming needs a SpecTree Toolbox role on a project. Ask an Admin or your Project Manager for a role.
            </p>
            <Button asChild variant="outline" size="sm">
              <Link to="/dashboard">Back to dashboard</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}
